import { useEffect, useMemo, useState, useCallback, type Dispatch, type SetStateAction } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Card, Button, Stack, Input, Select, useToast, Cluster } from "@salesapp/ui";
import { crmApi, type Lead, type TimelineEvent } from "../../lib/crmApi";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import { RecordTimeline } from "./RecordTimeline";
import { opsApi, type AppLayout, type FieldDef } from "../../lib/opsApi";
import { useAccess } from "../../shell/AccessProvider";
import { LEAD_SOURCES, leadDisplayName } from "./LeadCaptureForm";
import { DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { DesignedRecordFields } from "./DesignedRecordFields";
import { withNormalizedLayout } from "./recordLayout";
import { useCrmHref } from "../../lib/customerUrl";
import { ProductPicker, type AppliedAgreementLine } from "./ProductPicker";
import { DisqualifyLeadModal } from "./DisqualifyLeadModal";
import { RevertToLeadModal } from "./RevertToLeadModal";
import { useLocale } from "../../shell/LocaleProvider";
import { FIELD_HINTS, LEAD_STATUS_OPTIONS, LEAD_TEMPERATURE_OPTIONS } from "../../help/fieldHints";
import { StatusBadge } from "./StatusBadge";
import { RecordOwner } from "./RecordOwner";
import { ActivityCommentForm } from "./ActivityCommentForm";
import { ContactLookup } from "./ContactLookup";
import { statusLabel } from "./SearchBar";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

type Tab = "details";

const OPEN_LEAD_STATUSES: Array<{ value: string; label: string }> = [
  { value: "new", label: "New" },
  { value: "working", label: "Working" },
  { value: "qualified", label: "Qualified" },
];

const READONLY_LEAD_KEYS = new Set([
  "lead_code",
  "lead_status",
  "converted_at",
  "converted_to_opportunity_id",
  "disqualification_reason",
  "company_id",
]);

const EDITABLE_LEAD_KEYS = new Set([
  "first_name",
  "last_name",
  "email",
  "phone",
  "company_name",
  "rating",
  "lead_score",
  "lead_source",
  "expected_close_date",
  "next_action_at",
  "next_action_type",
  "notes",
]);

const NEXT_ACTION_TYPES = ["call", "email", "meeting", "task"];

function localDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function noonUtc(day: string | null): string | null {
  if (!day) return null;
  return `${day}T12:00:00Z`;
}

function formFromLead(lead: Lead): Record<string, string> {
  return {
    first_name: lead.first_name ?? "",
    last_name: lead.last_name ?? "",
    email: lead.email ?? "",
    phone: lead.phone ?? "",
    contact_id: lead.contact_id ?? "",
    company_name: lead.company_name ?? "",
    rating: lead.rating ?? "warm",
    lead_score: lead.lead_score != null ? String(lead.lead_score) : "",
    lead_source: lead.lead_source ?? "",
    expected_close_date: lead.expected_close_date?.slice(0, 10) ?? "",
    next_action_at: localDateInput(lead.next_action_at),
    next_action_type: lead.next_action_type ?? "",
    notes: lead.notes ?? "",
  };
}

function leadPatch(form: Record<string, string>) {
  const blank = (key: string) => form[key].trim() || null;
  const scoreRaw = form.lead_score.trim();
  const leadScore = scoreRaw === "" ? null : Number(scoreRaw);
  return {
    first_name: blank("first_name"),
    last_name: blank("last_name"),
    email: blank("email"),
    phone: blank("phone"),
    contact_id: blank("contact_id"),
    company_name: blank("company_name"),
    rating: blank("rating") ?? "warm",
    lead_score: leadScore != null && Number.isFinite(leadScore) ? Math.max(0, Math.min(100, leadScore)) : null,
    lead_source: blank("lead_source"),
    expected_close_date: blank("expected_close_date"),
    next_action_at: noonUtc(blank("next_action_at")),
    next_action_type: blank("next_action_type"),
    notes: blank("notes"),
  };
}

function leadAgeLabel(createdAt: string | null | undefined): string | undefined {
  if (!createdAt) return undefined;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return undefined;
  const today = new Date();
  const createdDay = Date.UTC(created.getFullYear(), created.getMonth(), created.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.max(0, Math.floor((todayDay - createdDay) / 86_400_000));
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function LeadFieldControl({
  fieldKey,
  label,
  field,
  formatted,
  form,
  setForm,
  orgId,
  companyId,
}: {
  fieldKey: string;
  label: string;
  field?: FieldDef;
  formatted: string;
  form: Record<string, string>;
  setForm: Dispatch<SetStateAction<Record<string, string>>>;
  orgId: string;
  companyId?: string | null;
}) {
  if (READONLY_LEAD_KEYS.has(fieldKey) || !EDITABLE_LEAD_KEYS.has(fieldKey)) {
    return formatted;
  }
  const value = form[fieldKey] ?? "";
  function setValue(next: string) {
    setForm((current) => ({ ...current, [fieldKey]: next }));
  }

  if (fieldKey === "first_name") {
    const fullName = [form.first_name, form.last_name].filter(Boolean).join(" ");
    return (
      <ContactLookup
        orgId={orgId}
        companyId={companyId || undefined}
        value={fullName}
        contactId={form.contact_id ?? ""}
        onChange={({ name, contactId, email }) => {
          const trimmed = name.trim();
          const [first, ...rest] = trimmed.split(/\s+/);
          setForm((current) => ({
            ...current,
            first_name: trimmed ? first ?? "" : "",
            last_name: rest.join(" "),
            contact_id: contactId,
            email: contactId ? email : current.email,
          }));
        }}
      />
    );
  }

  if (fieldKey === "last_name" || fieldKey === "email") {
    return value || "—";
  }

  if (fieldKey === "rating") {
    return (
      <Select aria-label={label} value={value || "warm"} onChange={(event) => setValue(event.target.value)}>
        {LEAD_TEMPERATURE_OPTIONS.map((option) => (
          <option key={option.label} value={option.label.toLowerCase()}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }

  if (fieldKey === "next_action_type") {
    const extras = value && !NEXT_ACTION_TYPES.includes(value) ? [value] : [];
    return (
      <Select aria-label={label} value={value} onChange={(event) => setValue(event.target.value)}>
        <option value="">—</option>
        {[...NEXT_ACTION_TYPES, ...extras].map((option) => (
          <option key={option} value={option}>
            {statusLabel(option)}
          </option>
        ))}
      </Select>
    );
  }

  if (fieldKey === "lead_source") {
    return (
      <Select aria-label={label} value={value} onChange={(event) => setValue(event.target.value)}>
        <option value="">—</option>
        {LEAD_SOURCES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }

  if (fieldKey === "notes" || field?.field_type === "textarea") {
    return (
      <div className="crm-resizable-field-shell">
        <textarea
          aria-label={label}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={4}
        />
      </div>
    );
  }

  return (
    <Input
      aria-label={label}
      type={
        fieldKey === "email" || field?.field_type === "email"
          ? "email"
          : fieldKey === "phone" || field?.field_type === "phone"
            ? "tel"
            : fieldKey === "lead_score" || field?.field_type === "number"
              ? "number"
              : fieldKey === "expected_close_date" || fieldKey === "next_action_at"
                ? "date"
                : "text"
      }
      value={value}
      onChange={(event) => setValue(event.target.value)}
    />
  );
}

export function LeadDetailPage({ orgId }: { orgId: string }) {
  const { leadId } = useParams<{ leadId: string }>();
  const { can } = useAccess();
  const { show } = useToast();
  const { formatDateTime, formatDate, formatMoney } = useLocale();
  const crmHref = useCrmHref();
  const navigate = useNavigate();
  const canEdit = can("crm.leads", "update");
  const canRevertOpportunity = can("crm.opportunities", "update");
  const [lead, setLead] = useState<Lead | null>(null);
  useLoxleyOffer(lead ? { kind: "lead", id: lead.id, label: leadDisplayName(lead) } : null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("details");
  const [recordLayout, setRecordLayout] = useState<AppLayout | null>(null);
  const [productIds, setProductIds] = useState<string[]>([]);
  const appliedAgreementLines = useMemo(() => {
    const map: Record<string, AppliedAgreementLine> = {};
    for (const item of lead?.products ?? []) {
      if (item.agreement_id) {
        map[item.id] = {
          line_code: item.agreement_line_code,
          agreement_id: item.agreement_id,
          agreement_number: item.agreement_number,
        };
      }
    }
    return map;
  }, [lead?.products]);
  const [savingProducts, setSavingProducts] = useState(false);
  const [disqualifyOpen, setDisqualifyOpen] = useState(false);
  const [disqualifyComment, setDisqualifyComment] = useState("");
  const [revertOpen, setRevertOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [convertValue, setConvertValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [replyText, setReplyText] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);
  const [sendingReply, setSendingReply] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const [data, designed, timelineRows] = await Promise.all([
        crmApi.getLead(leadId, orgId),
        opsApi.getLayout(orgId, "lead").then(withNormalizedLayout).catch(() => null),
        crmApi.getLeadTimeline(leadId, orgId).catch(() => []),
      ]);
      setLead(data);
      setRecordLayout(designed);
      setTimeline(timelineRows);
      setProductIds((data.products ?? []).map((item) => item.id));
      setForm(formFromLead(data));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load lead");
    } finally {
      setLoading(false);
    }
  }, [leadId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const originalEmail = timeline.some((event) => event.event_type === "email") || lead?.lead_source === "mailbox";

  const sendReply = async () => {
    if (!leadId || !replyText.trim()) {
      setReplyError("Write the reply before sending.");
      return;
    }
    setSendingReply(true);
    setReplyError(null);
    try {
      await crmApi.replyToLead(leadId, replyText.trim(), orgId);
      setReplyText("");
      show("Reply sent", "success");
      await load();
    } catch (e) {
      setReplyError(reportSaveFailure(show, e, "The reply could not be sent."));
    } finally {
      setSendingReply(false);
    }
  };

  const handleConvert = async () => {
    if (!leadId) return;
    const known = lead?.amount != null && lead.amount > 0 ? lead.amount : Number(convertValue);
    if (!Number.isFinite(known) || known <= 0) {
      setError(reportSaveBlocked(show, "An opportunity needs a value greater than zero."));
      return;
    }
    try {
      const result = await crmApi.convertLead(
        leadId, true, undefined, orgId, known, lead?.currency || undefined,
      );
      show("Converted to opportunity", "success");
      if (result.opportunity_id) {
        navigate(crmHref(`/opportunities/${result.opportunity_id}`));
        return;
      }
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to convert lead"));
    }
  };

  const revertToLead = async () => {
    if (!lead?.converted_to_opportunity_id) return;
    setReverting(true);
    setError(null);
    try {
      await crmApi.revertOpportunityToLead(lead.converted_to_opportunity_id, orgId);
      setRevertOpen(false);
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to move this deal back to a lead"));
    } finally {
      setReverting(false);
    }
  };

  const saveProducts = async (nextIds: string[]) => {
    if (!leadId || !canEdit) return;
    setProductIds(nextIds);
    setSavingProducts(true);
    setError(null);
    try {
      await crmApi.updateLead(leadId, { product_ids: nextIds }, orgId);
      show("Products saved", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to save products"));
      await load();
    } finally {
      setSavingProducts(false);
    }
  };

  const disqualify = async () => {
    if (!leadId || !disqualifyComment.trim()) return;
    try {
      await crmApi.changeLeadStatus(leadId, "disqualified", orgId, disqualifyComment.trim());
      setDisqualifyOpen(false);
      setDisqualifyComment("");
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to disqualify lead"));
    }
  };

  const moveStage = async (nextStatus: string) => {
    if (!leadId || !lead) return;
    const current = lead.lead_status ?? "new";
    if (nextStatus === current) return;
    if (nextStatus === "converted") {
      await handleConvert();
      return;
    }
    if (nextStatus === "disqualified") {
      setDisqualifyOpen(true);
      return;
    }
    try {
      await crmApi.changeLeadStatus(leadId, nextStatus, orgId);
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to move lead"));
    }
  };

  const startEdit = () => {
    if (!lead) return;
    setForm(formFromLead(lead));
    setEditing(true);
    setTab("details");
  };

  const cancelEdit = () => {
    if (lead) setForm(formFromLead(lead));
    setEditing(false);
  };

  const saveLead = async () => {
    if (!leadId || !canEdit) return;
    setSaving(true);
    setError(null);
    try {
      await crmApi.updateLead(leadId, leadPatch(form), orgId);
      setEditing(false);
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to save lead"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card>Loading...</Card>;
  if (error && !lead) return <Card className="crm-error">{error}</Card>;
  if (!lead) return <Card>Lead not found</Card>;

  const fullName = leadDisplayName(lead);
  const converted = Boolean(lead.converted_at);
  const amountLabel =
    lead.amount != null ? formatMoney(lead.amount, lead.currency, true) : undefined;
  const canEditDetails = canEdit && !converted;
  const ageLabel = leadAgeLabel(lead.created_at);

  return (
    <RecordWorkspace
      kind="lead"
      objectLabel="Lead"
      title={fullName}
      subtitle={lead.company_name || undefined}
      backTo={crmHref("/leads")}
      backLabel="← Back to Leads"
      highlights={[
        { label: "Lead ID", value: lead.lead_code },
        { label: "Status", value: <StatusBadge status={lead.lead_status ?? "new"} options={LEAD_STATUS_OPTIONS} /> },
        { label: "Amount", value: amountLabel },
        {
          label: "Temperature",
          value: (
            <StatusBadge
              status={lead.rating ?? "cold"}
              options={LEAD_TEMPERATURE_OPTIONS}
              className={"crm-badge crm-badge--" + (lead.rating ?? "cold")}
            />
          ),
        },
        { label: "Company", value: lead.company_name },
        { label: "Source", value: lead.lead_source },
        {
          label: "Salesperson",
          value: (
            <RecordOwner
              orgId={orgId}
              kind="leads"
              recordId={lead.id}
              ownerUserId={lead.owner_user_id}
              ownerName={lead.owner_name}
              canReassign={lead.can_reassign_owner}
              onChanged={(owner) => setLead((prev) => (prev ? { ...prev, ...owner } : prev))}
            />
          ),
        },
        { label: "Score", value: lead.lead_score != null ? String(lead.lead_score) : undefined },
        { label: "Email", value: lead.email },
        ...(ageLabel
          ? [{ label: "Aging", value: <span className="crm-badge crm-badge--aging">{ageLabel}</span> }]
          : []),
      ]}
      actions={
        <ResponsiveActions
          primary={
            <>
              {!converted && canEdit ? (
                <Select
                  aria-label="Move lead to stage"
                  value={lead.lead_status ?? "new"}
                  onChange={(event) => void moveStage(event.target.value)}
                >
                  {OPEN_LEAD_STATUSES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  <option value="converted">Converted</option>
                  <option value="disqualified">Disqualified</option>
                </Select>
              ) : null}
              {!converted ? (
                <>
                  {lead.amount == null || lead.amount <= 0 ? (
                    <Input
                      type="number"
                      min={0}
                      aria-label="Deal value"
                      placeholder="Deal value"
                      value={convertValue}
                      onChange={(event) => setConvertValue(event.target.value)}
                    />
                  ) : null}
                  <Button onClick={() => handleConvert()}>Convert to opportunity</Button>
                </>
              ) : (
                <span className="crm-badge crm-badge--active">Converted</span>
              )}
            </>
          }
          more={
            <>
              {!converted && canEdit ? (
                <Button variant="secondary" onClick={() => setDisqualifyOpen(true)}>Disqualify</Button>
              ) : null}
              {converted && canRevertOpportunity && lead.converted_to_opportunity_id ? (
                <Button variant="secondary" onClick={() => setRevertOpen(true)}>
                  Move back to lead
                </Button>
              ) : null}
              <DeleteRecordButton
                capability="crm.leads"
                recordLabel={fullName}
                onDeleted={async () => {
                  if (!leadId) return;
                  try {
                    await crmApi.deleteLead(leadId, orgId);
                    navigate(crmHref("/leads"));
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Failed to delete lead");
                  }
                }}
              />
            </>
          }
        />
      }
      tabs={[
        { key: "details", label: "Details" },
      ]}
      activeTab={tab}
      onTabChange={(key) => setTab(key as Tab)}
      aside={
        <>
          <div className="crm-aside-panel">
            <Cluster gap={2} justify="between" align="center">
              <h2 className="crm-aside-title">Stage history</h2>
              {lead.company_id ? (
                <Button variant="ghost" size="sm" onClick={() => navigate(crmHref(`/companies/${lead.company_id}?tab=activities`))}>
                  View activities
                </Button>
              ) : null}
            </Cluster>
            <div className="crm-quick-links">
              {lead.company_id ? (
                <Link to={crmHref(`/companies/${lead.company_id}`)} className="crm-quick-link">
                  <strong>1</strong>
                  Company
                </Link>
              ) : (
                <div className="crm-quick-link">
                  <strong>0</strong>
                  Company
                </div>
              )}
              {lead.converted_to_opportunity_id ? (
                <Link to={crmHref(`/opportunities/${lead.converted_to_opportunity_id}`)} className="crm-quick-link">
                  <strong>1</strong>
                  Opportunity
                </Link>
              ) : (
                <div className="crm-quick-link">
                  <strong>{lead.converted_at ? "Yes" : "No"}</strong>
                  Converted
                </div>
              )}
            </div>
          </div>
          <div className="crm-aside-panel">
            <h2 className="crm-aside-title">Lead timeline</h2>
            {canEdit && originalEmail ? (
              <div className="crm-panel-card">
                <Stack gap={3}>
                  <h2 className="crm-section-title">Reply to the original email</h2>
                  {replyError ? <p className="crm-error">{replyError}</p> : null}
                  <label className="crm-field">
                    <span>Your reply</span>
                    <textarea
                      className="crm-textarea crm-textarea--activity"
                      value={replyText}
                      onChange={(event) => setReplyText(event.target.value)}
                      rows={5}
                    />
                  </label>
                  <div className="crm-form-actions">
                    <Button onClick={() => void sendReply()} disabled={sendingReply}>
                      {sendingReply ? "Sending..." : "Send reply"}
                    </Button>
                  </div>
                </Stack>
              </div>
            ) : null}
            <RecordTimeline
              events={timeline}
              emptyTitle="No timeline events yet"
              emptyDescription="Stage and status moves for this lead will appear here."
            />
          </div>
        </>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {tab === "details" && (
        <>
          <ActivityCommentForm
            orgId={orgId}
            companyId={lead.company_id}
            leadId={lead.id}
            defaultSubject={lead.lead_code ? `Comment on Lead ID: ${lead.lead_code}` : "Comment on lead"}
            onSaved={load}
          />
          {canEditDetails ? (
            <div className="crm-toolbar crm-toolbar--end">
              {editing ? (
                <Cluster gap={2}>
                  <Button onClick={() => saveLead()} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                </Cluster>
              ) : (
                <Button onClick={startEdit}>Edit</Button>
              )}
            </div>
          ) : null}
          {recordLayout ? (
            <DesignedRecordFields
              layout={recordLayout}
              record={lead as unknown as Record<string, unknown>}
              hideEmptySections={false}
              renderValue={
                editing
                  ? ({ fieldKey, formatted, field }) => (
                      <LeadFieldControl
                        fieldKey={fieldKey}
                        label={field?.label ?? fieldKey}
                        field={field}
                        formatted={formatted}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    )
                  : ({ fieldKey, formatted }) =>
                      fieldKey === "company_name" && lead.company_id ? (
                        <Link to={crmHref(`/companies/${lead.company_id}`)}>{formatted}</Link>
                      ) : fieldKey === "first_name" && lead.contact_id && lead.company_id ? (
                        <Link to={crmHref(`/companies/${lead.company_id}?contact=${lead.contact_id}`)}>{formatted}</Link>
                      ) : (
                        formatted
                      )
              }
            />
          ) : (
            <>
              <DetailSection title="Lead information">
                <FieldItem label="Lead ID" value={lead.lead_code} />
                <FieldItem
                  label="First name"
                  hint={FIELD_HINTS.lead_first_name}
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="first_name"
                        label="First name"
                        formatted={lead.first_name || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : lead.contact_id && lead.company_id ? (
                      <Link to={crmHref(`/companies/${lead.company_id}?contact=${lead.contact_id}`)}>
                        {lead.first_name || "Open contact"}
                      </Link>
                    ) : (
                      lead.first_name || "— (not known yet)"
                    )
                  }
                />
                <FieldItem
                  label="Last name"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="last_name"
                        label="Last name"
                        formatted={lead.last_name || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.last_name || "— (not known yet)"
                    )
                  }
                />
                <FieldItem
                  label="Email"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="email"
                        label="Email"
                        formatted={lead.email || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.email
                    )
                  }
                />
                <FieldItem
                  label="Phone"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="phone"
                        label="Phone"
                        formatted={lead.phone || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.phone
                    )
                  }
                />
                <FieldItem label="Status" value={lead.lead_status} />
                <FieldItem label="Amount" value={amountLabel} />
                <FieldItem
                  label="Temperature"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="rating"
                        label="Temperature"
                        formatted={lead.rating || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.rating
                    )
                  }
                />
                <FieldItem
                  label="Score"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="lead_score"
                        label="Score"
                        formatted={lead.lead_score != null ? String(lead.lead_score) : "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : lead.lead_score != null ? (
                      String(lead.lead_score)
                    ) : undefined
                  }
                />
                <FieldItem
                  label="Source"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="lead_source"
                        label="Source"
                        formatted={lead.lead_source || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.lead_source
                    )
                  }
                />
                <FieldItem
                  label="Company"
                  value={
                    lead.company_id ? (
                      <Link to={crmHref(`/companies/${lead.company_id}`)}>{lead.company_name ?? "Open company"}</Link>
                    ) : (
                      lead.company_name
                    )
                  }
                />
                <FieldItem
                  label="Expected close"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="expected_close_date"
                        label="Expected close"
                        formatted={lead.expected_close_date?.slice(0, 10) || "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.expected_close_date?.slice(0, 10)
                    )
                  }
                />
                <FieldItem
                  label="Next action"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="next_action_type"
                        label="Next action"
                        formatted={lead.next_action_type ? statusLabel(lead.next_action_type) : "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.next_action_type ? statusLabel(lead.next_action_type) : "—"
                    )
                  }
                />
                <FieldItem
                  label="Next action date"
                  value={
                    editing ? (
                      <LeadFieldControl
                        fieldKey="next_action_at"
                        label="Next action date"
                        formatted={lead.next_action_at ? formatDate(lead.next_action_at) : "—"}
                        form={form}
                        setForm={setForm}
                        orgId={orgId}
                        companyId={lead.company_id}
                      />
                    ) : (
                      lead.next_action_at ? formatDate(lead.next_action_at) : "—"
                    )
                  }
                />
              </DetailSection>
              {lead.notes || editing ? (
                <DetailSection title="Context">
                  <FieldItem
                    label="Notes"
                    value={
                      editing ? (
                        <LeadFieldControl
                          fieldKey="notes"
                          label="Notes"
                          formatted={lead.notes || "—"}
                          form={form}
                          setForm={setForm}
                          orgId={orgId}
                          companyId={lead.company_id}
                        />
                      ) : (
                        lead.notes
                      )
                    }
                  />
                </DetailSection>
              ) : null}
              {lead.disqualification_reason ? (
                <DetailSection title="Disqualified">
                  <FieldItem label="Comment" value={lead.disqualification_reason} />
                </DetailSection>
              ) : null}
              {lead.converted_at ? (
                <DetailSection title="Conversion">
                  <FieldItem label="Converted at" value={formatDateTime(lead.converted_at)} />
                  {lead.converted_to_opportunity_id ? (
                    <FieldItem
                      label="Opportunity"
                      value={<Link to={crmHref(`/opportunities/${lead.converted_to_opportunity_id}`)}>Open opportunity</Link>}
                    />
                  ) : null}
                </DetailSection>
              ) : null}
            </>
          )}
          <div className="crm-panel-card">
            <Stack gap={4}>
              <h2 className="crm-section-title">Products</h2>
              <ProductPicker
                orgId={orgId}
                value={productIds}
                applied={appliedAgreementLines}
                disabled={!canEdit || converted}
                onChange={(ids: string[]) => {
                  if (canEdit && !converted) void saveProducts(ids);
                  else setProductIds(ids);
                }}
              />
              {savingProducts ? <p className="crm-muted">Saving products…</p> : null}
            </Stack>
          </div>
        </>
      )}

      <DisqualifyLeadModal
        open={disqualifyOpen}
        comment={disqualifyComment}
        onCommentChange={setDisqualifyComment}
        onCancel={() => setDisqualifyOpen(false)}
        onConfirm={() => disqualify()}
      />
      <RevertToLeadModal
        open={revertOpen}
        leadLabel={lead.lead_code}
        saving={reverting}
        onCancel={() => setRevertOpen(false)}
        onConfirm={() => revertToLead()}
      />
    </RecordWorkspace>
  );
}
